import { CloseCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Input } from 'antd'
import { useTranslation } from 'react-i18next'
import type {
  VirtualIpAnswers,
  VirtualIpQuestion,
  VirtualIpQuestionOption,
  VirtualIpQuestionType,
  VirtualIpSubField,
} from '../../api'

const { TextArea } = Input

/** VISTOERA_VIRTUAL_IP_POSITIONING_STEP1_V1 §5 — ONE generic renderer for
 * every question `type`, driven entirely by the backend-seeded schema
 * (spec §9: never a bespoke per-question React component). Each of the
 * 9 default questions is just data passed through this same switch. */

interface OptionChipsProps {
  type: 'single_select' | 'multi_select'
  options: VirtualIpQuestionOption[]
  value: unknown
  maxSelections?: number | null
  onChange: (next: string[] | string) => void
  disabled?: boolean
}

function OptionChips({ type, options, value, maxSelections, onChange, disabled }: OptionChipsProps) {
  if (type === 'single_select') {
    const current = typeof value === 'string' ? value : ''
    return (
      <div className="tc-vip-options">
        {options.map((opt) => (
          <span
            key={opt.value}
            role="button"
            className={`tc-vip-chip${current === opt.value ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}`}
            onClick={() => !disabled && onChange(opt.value)}
          >
            {opt.label}
          </span>
        ))}
      </div>
    )
  }

  const current = Array.isArray(value) ? (value as string[]) : []
  const atMax = typeof maxSelections === 'number' && maxSelections > 0 && current.length >= maxSelections
  return (
    <div className="tc-vip-options">
      {options.map((opt) => {
        const selected = current.includes(opt.value)
        const optionDisabled = disabled || (!selected && atMax)
        return (
          <span
            key={opt.value}
            role="button"
            className={`tc-vip-chip${selected ? ' is-selected' : ''}${optionDisabled ? ' is-disabled' : ''}`}
            onClick={() => {
              if (optionDisabled) return
              onChange(selected ? current.filter((v) => v !== opt.value) : [...current, opt.value])
            }}
          >
            {opt.label}
          </span>
        )
      })}
    </div>
  )
}

function SubFieldControl({
  field,
  value,
  onChange,
}: {
  field: VirtualIpSubField
  value: unknown
  onChange: (next: unknown) => void
}) {
  const type = field.type as VirtualIpQuestionType
  if (type === 'multi_select' || type === 'single_select') {
    return (
      <OptionChips
        type={type}
        options={field.options ?? []}
        value={value}
        maxSelections={field.max_selections}
        onChange={(next) => onChange(next)}
      />
    )
  }
  return (
    <Input
      value={typeof value === 'string' ? value : ''}
      placeholder={field.placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

interface QuestionCardProps {
  question: VirtualIpQuestion
  answers: VirtualIpAnswers
  onAnswerChange: (key: string, value: unknown) => void
  showValidationError?: boolean
  className?: string
}

export default function QuestionCard({
  question,
  answers,
  onAnswerChange,
  showValidationError,
  className,
}: QuestionCardProps) {
  const { t } = useTranslation(['virtualIp', 'validation'])
  const value = answers[question.key]

  const renderMain = () => {
    switch (question.type) {
      case 'multi_select':
      case 'single_select':
        return (
          <OptionChips
            type={question.type}
            options={question.options ?? []}
            value={value}
            maxSelections={question.max_selections}
            onChange={(next) => onAnswerChange(question.key, next)}
          />
        )
      case 'text':
        return (
          <Input
            value={typeof value === 'string' ? value : ''}
            placeholder={question.placeholder}
            onChange={(e) => onAnswerChange(question.key, e.target.value)}
          />
        )
      case 'textarea':
        return (
          <div>
            <TextArea
              value={typeof value === 'string' ? value : ''}
              placeholder={question.placeholder}
              maxLength={question.max_length ?? undefined}
              autoSize={{ minRows: 3, maxRows: 6 }}
              onChange={(e) => onAnswerChange(question.key, e.target.value)}
            />
            {question.max_length ? (
              <div className="tc-vip-card-hint" style={{ textAlign: 'right' }}>
                {(typeof value === 'string' ? value.length : 0)} / {question.max_length}
              </div>
            ) : null}
          </div>
        )
      case 'composite': {
        const composite = (value && typeof value === 'object' ? (value as Record<string, unknown>) : {}) as Record<
          string,
          unknown
        >
        return (
          <>
            {(question.fields ?? []).map((field) => (
              <div className="tc-vip-subfield" key={field.key}>
                <div className="tc-vip-subfield-title">
                  {field.title}
                  {field.required ? <span className="tc-vip-card-required">*</span> : null}
                </div>
                <SubFieldControl
                  field={field}
                  value={composite[field.key]}
                  onChange={(next) => onAnswerChange(question.key, { ...composite, [field.key]: next })}
                />
              </div>
            ))}
          </>
        )
      }
      case 'repeatable_group': {
        const rows = Array.isArray(value) ? (value as Array<Record<string, string>>) : []
        const maxItems = question.repeatable_max_items ?? undefined
        const fields = question.repeatable_fields ?? []
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.map((row, idx) => (
              <div className="tc-vip-repeatable-row" key={idx}>
                {fields.map((f) => (
                  <Input
                    key={f.key}
                    value={row[f.key] ?? ''}
                    placeholder={f.placeholder}
                    aria-label={f.label}
                    onChange={(e) => {
                      const next = rows.slice()
                      next[idx] = { ...next[idx], [f.key]: e.target.value }
                      onAnswerChange(question.key, next)
                    }}
                  />
                ))}
                <Button
                  type="text"
                  icon={<CloseCircleOutlined />}
                  onClick={() => onAnswerChange(question.key, rows.filter((_, i) => i !== idx))}
                />
              </div>
            ))}
            {(!maxItems || rows.length < maxItems) && (
              <Button
                className="tc-vip-repeatable-add"
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => onAnswerChange(question.key, [...rows, {}])}
              >
                {maxItems
                  ? t('virtualIp:common.addItemWithCount', { current: rows.length, max: maxItems })
                  : t('virtualIp:common.addItem')}
              </Button>
            )}
          </div>
        )
      }
      default:
        return null
    }
  }

  const conditional = question.conditional_visibility
  const mainValues = Array.isArray(value) ? (value as string[]) : typeof value === 'string' ? [value] : []
  const showConditional = conditional && mainValues.includes(conditional.trigger_option)

  return (
    <div className={`tc-data-card tc-vip-card${className ? ` ${className}` : ''}`}>
      <div className="tc-vip-card-head">
        <div className="tc-vip-card-title">
          {question.title}
          {question.required ? <span className="tc-vip-card-required">*</span> : null}
        </div>
        {question.max_selections ? (
          <span className="tc-vip-card-hint">{t('virtualIp:common.maxSelections', { count: question.max_selections })}</span>
        ) : null}
      </div>
      {question.description ? <p className="tc-vip-card-desc">{question.description}</p> : null}

      {renderMain()}

      {showConditional && conditional ? (
        <div className="tc-vip-conditional">
          <Input
            placeholder={conditional.field_placeholder || conditional.field_label}
            value={typeof answers[conditional.field_key] === 'string' ? (answers[conditional.field_key] as string) : ''}
            onChange={(e) => onAnswerChange(conditional.field_key, e.target.value)}
          />
        </div>
      ) : null}

      {question.secondary_field ? (
        <div className="tc-vip-secondary">
          <label className="tc-vip-secondary-label" htmlFor={`vip-${question.secondary_field.key}`}>
            {question.secondary_field.label}
          </label>
          <Input
            id={`vip-${question.secondary_field.key}`}
            value={
              typeof answers[question.secondary_field.key] === 'string'
                ? (answers[question.secondary_field.key] as string)
                : ''
            }
            placeholder={question.secondary_field.placeholder}
            maxLength={question.secondary_field.max_length ?? undefined}
            onChange={(e) => onAnswerChange(question.secondary_field!.key, e.target.value)}
          />
        </div>
      ) : null}

      {showValidationError ? (
        <div className="tc-vip-card-hint" style={{ color: 'var(--error-text)' }}>{t('validation:required')}</div>
      ) : null}
    </div>
  )
}
